import { ErrorBoundary } from "react-error-boundary";
import { Suspense, useState, useEffect, useRef, useMemo, useDeferredValue } from "react";
import { router } from "@inertiajs/react";
import "./applications.scss";
import useDevice from "@/hooks/use_device";
import ApplicationsList from "@/components/applications/applications_list";
import SelectedApplication from "@/components/applications/selected_application";
import { Row } from "@sprout/layout";
import Application from "@/types/application";
import { Error } from "@components/error";
import useApplications from "@/hooks/use_applications";
import LoadingSpinner from "@/sprout/loading_spinner";
import EmptyState from "@components/empty_state.tsx";
import Fingerprint1 from "@images/fingerprint_1.svg";
import useTranslation from "@hooks/use_translation.ts";
import { IconButton } from "@sprout/icon_button.tsx";
import BackIcon from "@sprout/icons/back.tsx";
import { useDebounce } from "react-use";
import { useSafePostHog } from "@hooks/use_posthog.ts";

type DeferredPageProps = {
  applications?: Application[];
};

type Props = DeferredPageProps & {
  selected_application_id?: number;
  selected_application?: Application;
  dream_job_tokens: number;
};

const Applications = ({
  selected_application_id: selectedApplicationId,
  selected_application: serverSelectedApplication,
  dream_job_tokens,
}: Props) => {
  const posthog = useSafePostHog();
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [sortBy, setSortBy] = useState<string>(
    () => new URLSearchParams(window.location.search).get("sort_by") ?? "",
  );

  const setSortByAndGetSorted = (value: string) => {
    setSortBy(value);
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set("sort_by", value);
      posthog?.capture("applications_sorted", { source: "applications", sort_by: value });
    } else {
      url.searchParams.delete("sort_by");
    }
    window.history.replaceState({}, "", url);
  };

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    300,
    [search],
  );

  const deferredSearch = useDeferredValue(debouncedSearch);
  const { isLoading, active, inactive } = useApplications({
    search: deferredSearch,
    sortBy: sortBy,
  });
  const device = useDevice();
  const { t } = useTranslation("applications");

  const activeApplications = active.applications;
  const inactiveApplications = inactive.applications;

  const selectedApplication = useMemo(() => {
    if (!selectedApplicationId) {
      return undefined;
    }

    const currentList = tab === "active" ? activeApplications : inactiveApplications;
    const found = currentList.find((app) => app.id === selectedApplicationId);
    return found
      ? { ...found, activityHistory: serverSelectedApplication?.activityHistory }
      : serverSelectedApplication;
  }, [
    selectedApplicationId,
    tab,
    activeApplications,
    inactiveApplications,
    serverSelectedApplication,
  ]);

  // Modified route function with check to prevent unnecessary navigation
  const navigateToApplication = (applicationId?: number | null) => {
    // Don't navigate if the ID is already the same
    if (applicationId === selectedApplicationId) {
      return;
    }

    const route = applicationId ? `/applications/${applicationId}` : "/applications";
    router.visit(route, {
      only: ["selected_application_id", "selected_application"],
      preserveState: true,
    });
  };

  const handleApplicationCardClick = (applicationId: number) => {
    if (applicationId !== selectedApplicationId) {
      const application =
        activeApplications.find((app) => app.id === applicationId) ||
        inactiveApplications.find((app) => app.id === applicationId);
      posthog?.capture("card_clicked", {
        card_type: "application",
        source: "applications",
        application_id: applicationId,
        job_title: application?.job_title,
        company_name: application?.company_name,
      });
    }
    navigateToApplication(applicationId);
  };

  const prevTabRef = useRef<string | null>(null);

  // Default to the first application in the tab when the tab value changes.
  useEffect(() => {
    // Skip if it's not actually a tab change (prevents running on other dependency changes)
    if (prevTabRef.current === tab) return;

    prevTabRef.current = tab;

    if (device.isWeb) {
      const currentList = tab === "active" ? activeApplications : inactiveApplications;

      const serverSelectionInTab =
        serverSelectedApplication &&
        (tab === "active"
          ? !serverSelectedApplication.inactive
          : serverSelectedApplication.inactive);

      const currentSelectionInTab =
        selectedApplicationId &&
        (currentList.find((app) => app.id === selectedApplicationId) || serverSelectionInTab);

      // Only select the first item if there's no valid selection in the current tab
      if (currentList.length > 0 && !currentSelectionInTab) {
        navigateToApplication(currentList[0].id);
      } else if (currentList.length === 0 && selectedApplicationId) {
        navigateToApplication(null);
      }
    }
  }, [tab, device, selectedApplicationId, activeApplications, inactiveApplications]);

  useEffect(() => {
    if (!selectedApplicationId || !serverSelectedApplication) return;

    const inActive = activeApplications.find((app) => app.id === selectedApplicationId);
    const inInactive = inactiveApplications.find((app) => app.id === selectedApplicationId);

    if (inActive || inInactive) return;

    if (!serverSelectedApplication.inactive && active.hasNextPage && !active.isFetchingNextPage) {
      active.fetchNextPage();
    } else if (
      serverSelectedApplication.inactive &&
      inactive.hasNextPage &&
      !inactive.isFetchingNextPage
    ) {
      inactive.fetchNextPage();
    }
  }, [
    selectedApplicationId,
    serverSelectedApplication,
    activeApplications,
    inactiveApplications,
    active.hasNextPage,
    active.isFetchingNextPage,
    inactive.hasNextPage,
    inactive.isFetchingNextPage,
  ]);

  const showApplicationsList = device.isWeb || !selectedApplication;
  const showSelectedApplication = device.isWeb || selectedApplication;

  const onViewParentApplication = async (applicationId: number) => {
    const parentApplication =
      activeApplications.find((app) => app.id === applicationId) ||
      inactiveApplications.find((app) => app.id === applicationId);

    if (parentApplication) {
      setTab(
        activeApplications.map((app) => app.id).includes(parentApplication.id)
          ? "active"
          : "inactive",
      );
      navigateToApplication(applicationId);
    }
  };

  return (
    <>
      {showApplicationsList && (
        <ApplicationsList
          activeApplications={activeApplications}
          inactiveApplications={inactiveApplications}
          isLoading={isLoading}
          tab={tab}
          setTab={setTab}
          selectedApplication={selectedApplication}
          onApplicationSelect={handleApplicationCardClick}
          hasTokens={dream_job_tokens > 0}
          search={search}
          setSearch={setSearch}
          setSortBy={setSortByAndGetSorted}
          totalActiveCount={active.count}
          totalInactiveCount={inactive.count}
          activeHasNextPage={active.hasNextPage ?? false}
          activeIsFetchingNextPage={active.isFetchingNextPage}
          onLoadMoreActive={() => active.fetchNextPage()}
          inactiveHasNextPage={inactive.hasNextPage ?? false}
          inactiveIsFetchingNextPage={inactive.isFetchingNextPage}
          onLoadMoreInactive={() => inactive.fetchNextPage()}
        />
      )}
      {showSelectedApplication && (
        <div className="selected-application-pane h-full overflow-auto">
          {selectedApplication && (
            <SelectedApplication
              application={selectedApplication}
              hasTokens={dream_job_tokens > 0}
              onViewParentApplication={onViewParentApplication}
            />
          )}
          {!isLoading && !selectedApplication && (
            <EmptyState svg={Fingerprint1} title={t("no_active")} message={t("know_whats_next")} />
          )}
        </div>
      )}
    </>
  );
};

const centeredState = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
};

const ErrorState = () => {
  return (
    <div style={centeredState}>
      <Error header={"Failed to fetch applications"} />
    </div>
  );
};

const LoadingState = () => {
  return (
    <div style={centeredState}>
      <LoadingSpinner size="medium" />
    </div>
  );
};

const ApplicationsPage = (props: Props) => {
  const device = useDevice();

  const goBack = () => {
    const route = "/applications";
    router.visit(route, {
      only: ["selected_application_id"],
      preserveState: true,
    });
  };

  return (
    <div className={device.isWeb ? "h-full overflow-hidden" : ""}>
      <main className="applications-page h-full">
        {!!props.selected_application_id && device.isMobile && (
          <IconButton icon={BackIcon} size="md" label="Back" onClick={goBack} />
        )}
        <Row gap="24px" className="h-full">
          <ErrorBoundary fallback={<ErrorState />}>
            <Suspense fallback={<LoadingState />}>
              <Applications {...props} />
            </Suspense>
          </ErrorBoundary>
        </Row>
      </main>
    </div>
  );
};

export default ApplicationsPage;
